import { FileUpload } from '@consigli/facade';
import {
  ExtendedFile,
  useGetPackagesByTypeQuery,
  useProject,
  useProjectId,
  useServiceName,
  useUpdatePackageMutation,
} from '@consigli/hooks';
import {
  DocumentPackageType,
  EmployerRole,
  INFRASTRUCTURE_ONLY_CONSTRUCTION_TYPES,
  PipelineName,
  ProcessorPreferenceKey,
  RfpRegion,
  ServiceName,
} from '@consigli/types';
import { useCallback, useState } from 'react';
import { FieldValues, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';

import { FormCheckbox } from '@/components/form-checkbox';
import { Route, ServiceIdIndexValue } from '@/routes';

import { PackagesDropdown } from './packages-dropdown';
import { AdvancedControls } from '../components/advanced-controls';
import { IterationName } from '../components/iteration-name';
import { SelectEmployerRole } from '../components/select-employer-role';
import { SelectLanguage } from '../components/select-language';
import { SelectRfpRegion } from '../components/select-rfp-region';
import { UploadIndexFiles } from '../components/upload-index-files';
import {
  DesignPackageReviewUploadInfo,
  InsightUploadInfo,
  TenderUploadInfo,
} from '../components/upload-info-panel';
import { useFetchValidateName } from '../components/user-input';
import { WizardAnalyzeButton } from '../components/wizard-analyze-button';
import { TabType, WizardLayout } from '../components/wizard-layout';
import { useCreateInsightPackage } from '../hooks/use-create-insight-package';
import { useUploadProgress } from '../hooks/use-upload-progress';

const TEXT_PROCESSING_SERVICES = new Set([ServiceName.DESIGN_PACKAGE_REVIEW]);

const getInsightPipeline = (serviceName: ServiceName): PipelineName =>
  TEXT_PROCESSING_SERVICES.has(serviceName)
    ? PipelineName.VALIDATION_TO_TEXT_PROCESSING
    : PipelineName.DEFAULT;

export const InsightWizard = () => {
  const serviceName = useServiceName();
  const [formData] = useState<FieldValues>({
    iterationName: '',
    language: '',
    needClassification: false,
    employerRole: serviceName === ServiceName.RFP_RFQ_REVIEW ? EmployerRole.CONSULTANT : null,
    rfpRegion: null,
  });
  const navigate = useNavigate();
  const { t } = useTranslation();
  const MAX_FILE_SIZE_BYTES = 600 * 1024 * 1024; // 600 MB
  const [localFiles, setLocalFiles] = useState<ExtendedFile[]>([]);
  const [indexFiles, setIndexFiles] = useState<ExtendedFile[]>([]);
  const [referenceDocs, setReferenceDocs] = useState<ExtendedFile[]>([]);
  const [checkListFiles, setCheckListFiles] = useState<ExtendedFile[]>([]);
  const projectId = useProjectId();
  const { project } = useProject(projectId);
  const type = ServiceIdIndexValue[serviceName] as DocumentPackageType;
  const [isProcessing, setIsProcessing] = useState(false);
  const [showMessagePanel, setShowMessagePanel] = useState(true);
  const [targetCounter, setTargetCounter] = useState(0);
  const [currentCounter, setCurrentCounter] = useState(0);
  const { createDocumentPackage, uploadFiles, startAnalysis, comparePackages } =
    useCreateInsightPackage({
      setCurrentCounter,
      setTargetCounter,
    });

  const [updateDocumentPackage] = useUpdatePackageMutation();

  const updatePackagePublishStatus = useCallback(
    (packageId: string) => {
      updateDocumentPackage({
        projectId,
        packageId,
        data: { published: false },
        isHidden: false,
      });
    },
    [projectId, updateDocumentPackage],
  );

  const { isUploading, progress } = useUploadProgress(currentCounter, targetCounter);

  const methods = useForm<FieldValues>({
    defaultValues: formData,
  });
  const { getValues, handleSubmit } = methods;
  const [uploadIndexFiles, setUploadIndexFiles] = useState<boolean>(false);
  const [selectedProcessors, setSelectedProcessors] = useState<ProcessorPreferenceKey[]>([]);

  const handleFileUpload = useCallback(
    async (packageId: string, referenceDocsPackageId?: string) => {
      if (packageId === undefined) return;

      const combinedFiles: (File & { isDrawingIndexList?: boolean; isChecklist?: boolean })[] = [
        ...localFiles,
      ];
      if (uploadIndexFiles) {
        const modifiedIndexFiles = indexFiles.map((file) => {
          // Directly assign properties to the file object (simpler and more reliable)
          return Object.assign(file, {
            isDrawingIndexList: true,
          });
        });
        combinedFiles.push(...modifiedIndexFiles);
      }
      if (checkListFiles.length > 0) {
        const modifiedCheckListFiles = checkListFiles.map((file) => {
          return Object.assign(file, {
            isChecklist: true,
          });
        });
        combinedFiles.push(...modifiedCheckListFiles);
      }

      await uploadFiles(combinedFiles, packageId);

      if (referenceDocsPackageId && referenceDocs.length > 0) {
        await uploadFiles(referenceDocs, referenceDocsPackageId);
      }
    },
    [checkListFiles, indexFiles, localFiles, referenceDocs, uploadFiles, uploadIndexFiles],
  );

  const processDocuments = useCallback(async () => {
    setIsProcessing(true);
    const rfpRegion = getValues('rfpRegion') as RfpRegion | null;
    const createdPackageId = await createDocumentPackage(
      type,
      getValues('iterationName'),
      getValues('language'),
      getValues('needClassification'),
      selectedProcessors.length > 0 ? selectedProcessors : undefined,
      getValues('employerRole'),
      serviceName === ServiceName.RFP_RFQ_REVIEW && rfpRegion ? { region: rfpRegion } : undefined,
    );

    if (!createdPackageId) {
      setIsProcessing(false);
      throw new Error('Failed to create package');
    }

    // step 2, upload files from local state of files
    await handleFileUpload(createdPackageId);

    // step 3, start pipeline
    await startAnalysis(createdPackageId, getInsightPipeline(serviceName));
  }, [
    createDocumentPackage,
    getValues,
    handleFileUpload,
    selectedProcessors,
    serviceName,
    startAnalysis,
    type,
  ]);

  const processTenderDocumentsWithReference = useCallback(async () => {
    setIsProcessing(true);
    const [createdPackageId, createReferencePackageId] = await Promise.all([
      createDocumentPackage(
        type,
        getValues('iterationName'),
        getValues('language'),
        undefined,
        selectedProcessors.length > 0 ? selectedProcessors : undefined,
        getValues('employerRole'),
      ),
      createDocumentPackage(
        type,
        `${getValues('iterationName')}-reference-material`,
        getValues('language'),
      ),
    ]);

    if (!createdPackageId || !createReferencePackageId) {
      throw new Error('Failed to create package');
    }

    // step 2, upload files from local state of files
    await handleFileUpload(createdPackageId, createReferencePackageId);

    // step 3, start pipeline for comparison
    await comparePackages(
      createdPackageId,
      createReferencePackageId,
      PipelineName.PACKAGE_COMPARISON,
      { skipSecondaryPackageProcessing: true },
    );
    navigate(
      `/${Route.PROJECTS}/${projectId}/${Route.SERVICES}/${serviceName}/${Route.PACKAGES}/${createdPackageId}/${Route.DASHBOARD}`,
    );
  }, [
    createDocumentPackage,
    type,
    getValues,
    selectedProcessors,
    handleFileUpload,
    comparePackages,
    navigate,
    projectId,
    serviceName,
  ]);

  const onAnalyze = useCallback(async () => {
    // step 1 , create package
    try {
      if (serviceName === ServiceName.TENDER_DOCUMENTS && referenceDocs.length > 0) {
        await processTenderDocumentsWithReference();
      } else {
        await processDocuments();
      }
      toast.success(t('package-creation.success-analyzed'));
    } catch (error) {
      console.log('Error starting analysis:', error);
      toast.error(t(`package-creation.failed-analyzed`));
    } finally {
      setIsProcessing(false);
    }
  }, [t, processDocuments, processTenderDocumentsWithReference, serviceName, referenceDocs]);

  const onCompare = useCallback(async () => {
    try {
      setIsProcessing(true);
      const selectedPackageId = getValues('package');
      if (!selectedPackageId) {
        toast.error(t('package-creation.no-package-selected'));
        return;
      }

      await uploadFiles(localFiles, selectedPackageId);

      await startAnalysis(selectedPackageId, PipelineName.COMPARISON);
      await updatePackagePublishStatus(selectedPackageId);
      toast.success(t('package-creation.comparison-started'));
    } catch (error) {
      console.log('Error starting comparison:', error);
      toast.error(t('package-creation.failed-compared'));
    } finally {
      setIsProcessing(false);
    }
  }, [getValues, uploadFiles, startAnalysis, localFiles, t, updatePackagePublishStatus]);

  const { validateName } = useFetchValidateName(useGetPackagesByTypeQuery, {
    projectId,
    type,
  });

  const compareInsightPackages = (
    <FormProvider {...methods}>
      <form className="w-full" onSubmit={handleSubmit(onCompare)}>
        <p className="mb-4 mt-4 text-base font-semibold text-day-neutral-subtle">
          {t('package-creation.select-package-to-compare')}
        </p>
        <PackagesDropdown />
        <p className="mb-4 mt-4 text-base font-semibold text-day-neutral-subtle">
          {t('package-creation.upload-files')}
        </p>
        <FileUpload
          files={localFiles}
          setFiles={setLocalFiles}
          dropAreaLabel={t('document-upload.drag-n-drop-here')}
          dropAreaClassName="mt-4 h-48 w-full"
          fileButtonLabel={t('document-upload.browse-files')}
          folderButtonLabel={t('document-upload.browse-folders')}
          uploadListLabel={t('document-upload.files')}
          zeroByteFilesMessage={t('document-upload.file-zero-bytes')}
          showMessagePanel={showMessagePanel}
          setShowMessagePanel={setShowMessagePanel}
          maxFileSizeBytes={MAX_FILE_SIZE_BYTES}
          fileSizeWarningText={t('document-upload.file-exceeds-max-size')}
        >
          <div className="fc-w-full">
            <WizardAnalyzeButton localFiles={localFiles} type="submit"></WizardAnalyzeButton>
          </div>
        </FileUpload>
      </form>
    </FormProvider>
  );

  const createInsightPackageForm = (
    <FormProvider {...methods}>
      <form className="w-full" onSubmit={handleSubmit(onAnalyze)}>
        <div className="flex flex-col" translate="no">
          <IterationName
            validateName={validateName}
            label={t('package-creation.analysis-name')}
            errorMessage={`${t('package-creation.analysis-name-empty')}*`}
          />
        </div>
        {serviceName !== ServiceName.DESIGN_PACKAGE_REVIEW &&
          serviceName !== ServiceName.RFP_RFQ_REVIEW && <SelectEmployerRole />}
        <SelectLanguage />
        {serviceName === ServiceName.RFP_RFQ_REVIEW && <SelectRfpRegion />}
        {serviceName === ServiceName.MANAGEMENT_OPERATIONS_MAINTENANCE && (
          <>
            <UploadIndexFiles
              uploadIndexFiles={uploadIndexFiles}
              setUploadIndexFiles={setUploadIndexFiles}
            />
            {uploadIndexFiles && (
              <>
                <FileUpload
                  files={indexFiles}
                  setFiles={setIndexFiles}
                  dropAreaLabel={t('document-upload.drag-n-drop-here')}
                  dropAreaClassName="mt-4 h-48 w-full"
                  fileButtonLabel={t('document-upload.browse-files')}
                  folderButtonLabel={t('document-upload.browse-folders')}
                  uploadListLabel={t('document-upload.files')}
                  zeroByteFilesMessage={t('document-upload.file-zero-bytes')}
                  showMessagePanel={showMessagePanel}
                  setShowMessagePanel={setShowMessagePanel}
                  maxFileSizeBytes={MAX_FILE_SIZE_BYTES}
                  fileSizeWarningText={t('document-upload.file-exceeds-max-size')}
                ></FileUpload>
              </>
            )}
          </>
        )}
        {(serviceName === ServiceName.MANAGEMENT_OPERATIONS_MAINTENANCE ||
          serviceName === ServiceName.TENDER_DOCUMENTS ||
          serviceName === ServiceName.DESIGN_PACKAGE_REVIEW) && (
          <AdvancedControls
            selectedProcessors={selectedProcessors}
            setSelectedProcessors={setSelectedProcessors}
            referenceDocs={referenceDocs}
            setReferenceDocs={setReferenceDocs}
            checkListFiles={checkListFiles}
            setCheckListFiles={setCheckListFiles}
            showMessagePanel={showMessagePanel}
            setShowMessagePanel={setShowMessagePanel}
          />
        )}
        <p className="mb-4 mt-4 text-base font-semibold text-day-neutral-subtle">
          {t('package-creation.upload-files')}
        </p>
        {serviceName === ServiceName.MANAGEMENT_OPERATIONS_MAINTENANCE &&
          !(
            project?.constructionTypes?.length &&
            project.constructionTypes.every((type) =>
              INFRASTRUCTURE_ONLY_CONSTRUCTION_TYPES.includes(type),
            )
          ) && (
            <FormCheckbox
              name="needClassification"
              labelKey="package-creation.need-classification"
            />
          )}
        <FileUpload
          files={localFiles}
          setFiles={setLocalFiles}
          dropAreaLabel={t('document-upload.drag-n-drop-here')}
          dropAreaClassName="mt-4 h-48 w-full"
          fileButtonLabel={t('document-upload.browse-files')}
          folderButtonLabel={t('document-upload.browse-folders')}
          uploadListLabel={t('document-upload.files')}
          zeroByteFilesMessage={t('document-upload.file-zero-bytes')}
          showMessagePanel={showMessagePanel}
          setShowMessagePanel={setShowMessagePanel}
          maxFileSizeBytes={MAX_FILE_SIZE_BYTES}
          fileSizeWarningText={t('document-upload.file-exceeds-max-size')}
        >
          <div className="fc-w-full">
            <WizardAnalyzeButton localFiles={localFiles} type="submit"></WizardAnalyzeButton>
          </div>
        </FileUpload>
      </form>
    </FormProvider>
  );

  const messageHeader =
    serviceName === ServiceName.DESIGN_PACKAGE_REVIEW ? (
      <DesignPackageReviewUploadInfo />
    ) : serviceName === ServiceName.TENDER_DOCUMENTS ? (
      <TenderUploadInfo />
    ) : (
      <InsightUploadInfo />
    );

  return (
    <WizardLayout
      isProcessing={isProcessing}
      messageHeader={messageHeader}
      isUploading={isUploading}
      progress={progress}
      tabContent={
        serviceName === ServiceName.TENDER_DOCUMENTS
          ? {
              [TabType.CREATE_PACKAGE]: createInsightPackageForm,
              [TabType.COMPARE_PACKAGE]: compareInsightPackages,
            }
          : {
              [TabType.CREATE_PACKAGE]: createInsightPackageForm,
            }
      }
    />
  );
};
